import React, { useEffect, useMemo, useState } from 'react';
import { selectHeaderStarredReportsByTypeAndTerm } from 'store/header/starred-reports/selectors/select-header-starred-reports';

import type { StarredReportsEvent } from 'features/star-report/types';
import type { ReportTypes } from 'core/constants/report-type';
import type { StarredReport } from 'core/entities';
import { useStarReportActions } from 'store/star-report/use-star-report-actions';
import {
    STARRED_REPORT_TABS,
    StarredReportsTabToReportTypeMap,
    type StarredReportTabsTypes,
} from 'features/star-report/components/starred-reports-tabs/starred-reports-tabs';
import { useSelectorWithProps } from 'utils/structured-selector/structured-selector';
import { getFeatureFlags } from 'core/flow-control';
import {
    BodyText,
    EmptyStateIllustration,
    FlexContainer,
    Icon,
    IconButton,
    Link,
    MultilineDropdownMenu,
    PopoverTemplate,
    SearchField,
    TertiaryTabs,
} from '@placer-ui/components';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { selectStarredReportAttributeCount } from 'features/my-zone/starred-reports/utils/select-starred-report-attribute-count/select-starred-report-attribute-count';
import { selectStarredReportDates } from 'features/my-zone/starred-reports/utils/select-starred-report-dates/select-starred-report-dates';
import { useAllStarredReportsUrl } from 'features/header/header-top-panel/starred-reports-button-deprecated/starred-reports-dialog/hooks/use-all-starred-reports-url';
import { openElevioComponent } from 'utils/elevio/elevio';

const starredReportListEventData = {
    component: 'quick-access',
};

const reportToIconType: Partial<Record<ReportTypes, string>> = {
    Property: 'Property',
    Chain: 'Chain',
    Industry: 'Factory',
    Market: 'Market',
};

const ALL_REPORTS_URL = 'my-zone/starred-reports';

const StarredReportsEmptyBox = ({
    selectedTab,
    isFiltered,
}: {
    selectedTab: StarredReportTabsTypes;
    isFiltered: boolean;
}) => (
    <FlexContainer flexDirection="column" gap={8}>
        <EmptyStateIllustration size={329} />
        <FlexContainer
            flexDirection="column"
            gap={8}
            style={{ textAlign: 'center' }}
            padding="0 24px 24px"
        >
            <BodyText variant="bold">
                {isFiltered
                    ? 'No Reports Match Your Search.'
                    : "You haven't starred any reports yet"}
            </BodyText>
            {isFiltered ? (
                <BodyText>Try updating your search</BodyText>
            ) : (
                <BodyText>
                    To star a report click the{' '}
                    <span style={{ position: 'relative', top: 3 }}>
                        <Icon type="StarSquare" size={16} />
                    </span>{' '}
                    icon on the
                    {` ${
                        selectedTab === STARRED_REPORT_TABS.ALL
                            ? 'reports`s'
                            : selectedTab + ' Report'
                    } `}
                    toolbar.{' '}
                    <Link
                        hideUnderline
                        testId="starred-reports-learn-more"
                        onClick={() => openElevioComponent('95', 'article')}
                    >
                        Learn more
                    </Link>
                </BodyText>
            )}
        </FlexContainer>
    </FlexContainer>
);

export const StarredReportsBox = () => {
    const [selectedTab, setSelectedTab] = useState<StarredReportTabsTypes>(STARRED_REPORT_TABS.ALL);
    const [currentSearch, setCurrentSearch] = useState('');
    const [isBoxOpen, setIsBoxOpen] = useState(false);
    const { enable_market_section, enable_industry_section } = getFeatureFlags();

    const { getReports, openReport } = useStarReportActions();
    const sendEvent = useSplunkCallback<StarredReportsEvent>(starredReportListEventData);

    const reportType = StarredReportsTabToReportTypeMap[selectedTab];
    const url = useAllStarredReportsUrl(selectedTab);

    const starredReportsOptions = useMemo(
        () => ({
            term: currentSearch,
            filter: reportType,
        }),
        [currentSearch, reportType],
    );

    const reportList = useSelectorWithProps(
        starredReportsOptions,
        selectHeaderStarredReportsByTypeAndTerm,
    );

    useEffect(() => {
        getReports();
    }, [getReports]);

    useEffect(() => {
        if (!isBoxOpen) {
            setCurrentSearch('');
            setSelectedTab(STARRED_REPORT_TABS.ALL);
        }
    }, [isBoxOpen]);

    const onViewAllClick = () => {
        window.location.href = selectedTab === STARRED_REPORT_TABS.ALL ? ALL_REPORTS_URL : url;
    };

    const onOpenReport = (report: StarredReport) => {
        const { isSameDay } = selectStarredReportDates(report.entities);

        sendEvent({
            action: 'open-starred-report',
            url: window.location.href,
            custom_dates: !isSameDay,
            filters: selectStarredReportAttributeCount(report.entities),
        });

        openReport({
            report,
        });

        setIsBoxOpen(false);
    };

    return (
        <PopoverTemplate
            onOpenChange={setIsBoxOpen}
            open={isBoxOpen}
            backgroundColor="white"
            testId="starred-reports-box"
            align="end"
            header={
                <PopoverTemplate.Header
                    title="Starred Reports"
                    actionIconType="ArrowSquareOut"
                    onActionClick={onViewAllClick}
                />
            }
            trigger={
                <IconButton
                    size="small"
                    variant="ghost"
                    aria-label="starred reports"
                    Icon={
                        <Icon
                            type="StarSquare"
                            size={24}
                            color={isBoxOpen ? 'brand' : 'gray'}
                            allowHoverState
                        />
                    }
                />
            }
        >
            <FlexContainer flexDirection="column" gap={16} width={360}>
                <FlexContainer flexDirection="column" gap={16} padding="0 16px">
                    <TertiaryTabs
                        onTabChange={(tab) => setSelectedTab(tab as StarredReportTabsTypes)}
                        defaultTab={STARRED_REPORT_TABS.ALL}
                        testId="starred-reports-tabs"
                        hasBrandColor
                    >
                        <TertiaryTabs.List>
                            <TertiaryTabs.Trigger
                                id={STARRED_REPORT_TABS.ALL}
                                label={STARRED_REPORT_TABS.ALL}
                                testId={`starred-reports-tab-${STARRED_REPORT_TABS.ALL}`}
                            />
                            <TertiaryTabs.Trigger
                                id={STARRED_REPORT_TABS.PROPERTY}
                                label={STARRED_REPORT_TABS.PROPERTY}
                                testId={`starred-reports-tab-${STARRED_REPORT_TABS.PROPERTY}`}
                            />
                            <TertiaryTabs.Trigger
                                id={STARRED_REPORT_TABS.CHAIN}
                                label={STARRED_REPORT_TABS.CHAIN}
                                testId={`starred-reports-tab-${STARRED_REPORT_TABS.CHAIN}`}
                            />
                            {enable_market_section && (
                                <TertiaryTabs.Trigger
                                    id={STARRED_REPORT_TABS.MARKET}
                                    label={STARRED_REPORT_TABS.MARKET}
                                    testId={`starred-reports-tab-${STARRED_REPORT_TABS.MARKET}`}
                                />
                            )}
                            {enable_industry_section && (
                                <TertiaryTabs.Trigger
                                    id={STARRED_REPORT_TABS.INDUSTRY}
                                    label={STARRED_REPORT_TABS.INDUSTRY}
                                    testId={`starred-reports-tab-${STARRED_REPORT_TABS.INDUSTRY}`}
                                />
                            )}
                        </TertiaryTabs.List>
                    </TertiaryTabs>
                </FlexContainer>
                <FlexContainer flexDirection="column" gap={8}>
                    <FlexContainer padding="0 16px">
                        <SearchField
                            fullWidth
                            placeholder="Search Starred Reports"
                            testId="starred-reports-search"
                            value={currentSearch}
                            onChange={setCurrentSearch}
                        />
                    </FlexContainer>
                    <MultilineDropdownMenu
                        items={reportList.map((report) => ({
                            value: report.name,
                            label: report.name,
                            description: `Last opened ${report.last_viewed_date}`,
                            Icon: (
                                <Icon
                                    type={reportToIconType[report.type] || 'Property'}
                                    size={24}
                                    allowHoverState={false}
                                />
                            ),
                            onClick: () => onOpenReport(report),
                        }))}
                        searchTerm={currentSearch}
                        NoResultsItem={
                            <StarredReportsEmptyBox
                                selectedTab={selectedTab}
                                isFiltered={!!currentSearch}
                            />
                        }
                        maxVisibleItems={6}
                        isUsingNewIconInfra
                    />
                </FlexContainer>
            </FlexContainer>
        </PopoverTemplate>
    );
};
